import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    Input,
    ElementRef,
    OnInit,
    output,
    signal,
    ViewEncapsulation,
} from '@angular/core';

import { FiChecklist2ImportService } from '@fi-shared/checklist/fi-checklist2-shared/fi-checklist2-import.service';
import { IChecklistV2 } from '../../checklist-v2';
import { FiTooltipContentType } from '@fi-shared/fi-tooltip/fi-tooltip-enums';
import { TProcessMediaBankFileFn } from '@fi-shared/fi-insert-media/fi-file-upload-d';
import { FileType } from '@fi-shared/types';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { IChecklist2ImportWarning } from '@fi-shared/checklist/fi-checklist2-shared/fi-checklist2-import/fi-checklist2-import';
import { FiChecklist2ImportTextsService } from '@fi-shared/checklist/fi-checklist2-shared/fi-checklist2-import-texts.service';
import { FiChecklist2ImportWarningType } from '@fi-shared/checklist/fi-checklist2-shared/fi-checklist2-import/fi-checklist2-import-enums';

@Component({
    selector: 'fi-checklist2-import',
    templateUrl: './fi-checklist2-import.component.html',
    styleUrl: './fi-checklist2-import.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist2-import' },
    standalone: false,
})
export class FiChecklist2ImportComponent implements OnInit {
    @Input() wrapperElementRef: ElementRef<HTMLElement>;
    checklistImported = output<IChecklistV2>();

    private fiChecklist2ImportService = inject(FiChecklist2ImportService);
    private fiChecklist2ImportTextsService = inject(FiChecklist2ImportTextsService);
    private fiUserDataService = inject(FiUserDataService);
    private sanitizer = inject(DomSanitizer);
    protected excelExampleUrl: SafeResourceUrl;
    protected excelExampleFileName: string;

    protected importWarnings = signal<string[]>([]);
    protected showImportWarnings = computed<boolean>(() => {
        return this.importWarnings()?.length > 0;
    });

    ngOnInit(): void {
        const isSweUser = this.fiUserDataService.applicationLanguage === FiApplicationLanguageType.Swedish;
        this.excelExampleUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            isSweUser ? '/assets/Excel template thumbnail (SWE).png' : '/assets/Excel template thumbnail (ENG).png',
        );
        this.excelExampleFileName = isSweUser ? 'Checklist_Excel_Mall.xlsx' : 'Checklist_Excel_Template.xlsx';

        /*// todo for tests
        this.setImportWarnings([
            {
                WarningType: FiChecklist2ImportWarningType.InvalidSheetName,
                Value: 'I am invalid sheet',
            },
            {
                WarningType: FiChecklist2ImportWarningType.InvalidColumnHeader,
                ColumnStr: 'BE',
            },
            {
                WarningType: FiChecklist2ImportWarningType.InvalidColumnHeader,
                ColumnStr: 'AC',
            },
            {
                WarningType: FiChecklist2ImportWarningType.InvalidScoringValue,
                ColumnStr: 'BE',
                Row: 4,
            },
            {
                WarningType: FiChecklist2ImportWarningType.InvalidScoringValue,
                ColumnStr: 'BE',
                Row: 8,
            },
            {
                WarningType: FiChecklist2ImportWarningType.InvalidComplianceThreshold,
                ColumnStr: 'D',
                Row: 2,
            },
        ]);*/
    }

    protected onDownloadImportTemplateClick() {
        this.fiChecklist2ImportService.downloadChecklistExcelTemplate(this.excelExampleFileName);
    }

    protected readonly FiTooltipContentType = FiTooltipContentType;

    protected importChecklistFromExcel: TProcessMediaBankFileFn = (mbRef) => {
        return new Promise<void>((resolve, reject) => {
            this.fiChecklist2ImportService
                .importFromObjectStorage(mbRef.PhysicalFileName, mbRef.Extension)
                .then((importResult) => {
                    this.setImportWarnings(importResult.Warnings);
                    this.checklistImported.emit(importResult.Checklist);
                    resolve();
                }, reject);
        });
    };

    private setImportWarnings(warnings: IChecklist2ImportWarning[]) {
        const warningTexts = warnings.map((warning: IChecklist2ImportWarning) =>
            this.fiChecklist2ImportTextsService.getChecklistImportWarningText(warning),
        );
        this.importWarnings.set(warningTexts);
    }

    protected onClearUploadData() {
        this.setImportWarnings([]);
        this.checklistImported.emit(undefined);
    }

    protected readonly FileType = FileType;
}
